<template>
  <v-container
    class="text--primary"
    fluid
    style="min-height: 590px"
  >
    <validation-observer ref="observer">
      <v-row>
        <v-col cols="12">
          <h2 class="pb-2">
            {{ $t('label.contact_details') }}
          </h2>
          <i18n
            path="help.onboarding.account"
            tag="p"
            class="body-2 pb-2"
          >
            <span class="font-weight-medium">{{ $t('title.settings') }}→{{ $t('title.account') }}</span>
          </i18n>
        </v-col>

        <v-col
          cols="12"
          class="py-0"
        >
          <v-text-field
            v-model="a.accountName"
            :label="$t('label.restaurant_name')"
            :error-messages="errors"
            required
          />
        </v-col>
        <v-col
          cols="12"
          class="py-1"
        >
          <validated-text-field
            v-model="a.streetAddress"
            rules="required|min:2|max:255"
            :label="$t('label.street')"
          />
        </v-col>
        <v-col
          cols="8"
          class="py-1"
        >
          <validated-text-field
            v-model="a.city"
            rules="required|min:2|max:255"
            :label="$t('label.city')"
          />
        </v-col>
        <v-col
          cols="4"
          class="py-1"
        >
          <validated-text-field
            v-model="a.postalCode"
            rules="required|min:2|max:50"
            :label="$t('label.zipcode')"
          />
        </v-col>
        <v-col
          cols="8"
          class="py-1"
        >
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="a.countryCode"
              class="pt-0"
              :label="$t('label.country')"
              :error-messages="errors"
              :items="countries"
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="4"
          class="py-1"
        >
          <validated-text-field
            v-model="a.countryState"
            rules="required|min:2|max:50"
            :label="$t('label.state')"
          />
        </v-col>
        <v-col
          cols="6"
          class="py-0"
        >
          <validated-text-field
            v-model="a.phone"
            :rules="`max:50|validPhone:${defaultCC}`"
            :label="$t('label.phone')"
          />
        </v-col>
        <v-col
          cols="6"
          class="py-0"
        >
          <validated-text-field
            v-model="a.website"
            prefix="https://"
            hide-prefix-on-blur
            strip-prefix-from-value
            rules="max:255"
            :label="$t('label.website')"
          />
        </v-col>
        <row-form-divider class="py-5" />
        <v-col
          cols="6"
          class="py-0"
        >
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="a.locale"
              class="pt-0"
              :label="$t('label.language')"
              :items="locales"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="6"
          class="py-0"
        >
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="a.timeZoneId"
              class="pt-0"
              :label="$t('label.time_zone')"
              :items="timezones"
              :error-messages="errors"
            />
          </validation-provider>
        </v-col>
      </v-row>
    </validation-observer>
  </v-container>
</template>

<script lang="ts">
import httpClient from '@/api/http-client';
import ValidatedTextField from '@/components/forms/ValidatedTextField.vue';
import Account from '@/model/Account';
import Country from '@/model/Country';
import Locale from '@/model/Locale';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import TimeZone from '@/model/TimeZone';
import { performSaveAction } from '@/services/vue-utils';
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import toast from '@/plugins/toast-plugin/toast';

export default Vue.extend({
  name: 'WelcomeStepAccount',
  components: { ValidatedTextField },
  props: {

  },
  data() {
    return {
      a: new Account(),
      olda: new Account(),
      unwatch: Function as (() => void),
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.a, this.olda);
    },
    countries(): Country[] {
      return this.$tstore.getters.sortedCountries;
    },
    locales(): Locale[] {
      return this.$tstore.getters.sortedLocales;
    },
    timezones(): TimeZone[] {
      return this.$tstore.getters.sortedTimezones;
    },
    defaultCC(): string {
      return this.a.countryCode || '';
    },
  },
  mounted() {
    this.reset();
  },
  created() {
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => { this.reset(); });
  },
  beforeDestroy() {
    this.unwatch();
  },
  methods: {
    reset() {
      const a = this.$tstore.state.settings.account;
      this.a = cloneModel(a);
      this.olda = a;

      if (!this.a.countryCode) this.autofillCountry();
    },
    async autofillCountry() {
      try {
        const countryCode = await httpClient.getCountryCode();
        if (!countryCode || this.a.countryCode) return; // no country code detected or no need to update it any more
        Vue.set(this.a, 'countryCode', countryCode); // autofill country code
      } catch (e) {
        console.log('Autofill country code error: ', e);
      }
    },
    // eslint-disable-next-line vue/no-unused-properties
    async submit() {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) {
        toast.error(this.$i18n.tc('error.validation_failed'));
        return;
      }

      if (!this.isChanged) {
        this.$emit('advance');
        return;
      }

      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendAccount', { entity: this.a }),
      );
      if (ok) {
        this.reset();
        this.$emit('advance');
      }
    },
  },
});
</script>
